import Alpine, { AlpineComponent } from "alpinejs";
import { Basket, BasketItem, BasketService, CutMeasure, GoogleService } from "../api";
import { getOrderTotal } from "../utils/getOrderTotal.ts";
import { BasketNotificationStore } from "./stores/basketNotificationStore.ts";

type MiniCartComponent = AlpineComponent<{
	isCartOpen: boolean;
	openCart: () => void;
	closeCart: () => void;
	toggleCart: () => void;
	isWebCustomer: boolean;
	isCartInvalid: () => boolean;
	removeFromBasket: (lineNo: number) => Promise<void>;
	subtractQuantity: (item: BasketItem) => Promise<void>;
	addQuantity: (item: BasketItem) => Promise<void>;
	addToBasket: (partNo: string, mainPartNo: string, productType: string, quantity: number) => Promise<void>;
	updateBasketItem: (item: BasketItem) => Promise<void>;
	removeCutMeasure: (item: BasketItem, cutMeasure: CutMeasure) => Promise<void>;
	updateCutMeasure: (item: BasketItem) => Promise<void>;
	subtractCutMeasureQuantity: (item: BasketItem, cutMeasure: CutMeasure) => Promise<void>;
	addCutMeasureQuantity: (item: BasketItem, cutMeasure: CutMeasure) => Promise<void>;
	addToCartMaxQuantityWarning: boolean;
	isCutMeasureDisabled: (item: BasketItem, index: number) => boolean;
}>;

export default (basket: string, isWebCustomer: boolean): MiniCartComponent => {
	Alpine.store("basket", basket);
	let timeout: string | number | NodeJS.Timeout | undefined;
	const addToCartTracking = async (partNo: string, quantity: number) => {
		const addToCart = await GoogleService.addToCart({
			query: {
				partNo: partNo,
				quantity: quantity,
			},
		});
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({
			event: "add_to_cart",
			ecommerce: addToCart.data,
		});
	};
	return {
		addToCartMaxQuantityWarning: false,
		isWebCustomer: isWebCustomer,
		isCartOpen: false,
		isCartInvalid() {
			const basket = this.$store.basket as Basket;
			let isBasketInvalid = false;
			for (const item of basket.items) {
				if (item.cutMeasures && item.cutMeasures.length > 0) {
					const cutMeasures = item.cutMeasures;
					const totalLength = cutMeasures.reduce(
						(acc, x) => (acc += parseFloat(x.cutLength) * parseFloat(x.noOfPieces)),
						0
					);
					if (totalLength < 1) {
						isBasketInvalid = true;
						break;
					}
				}
			}

			return isBasketInvalid;
		},
		async init() {
			await getOrderTotal();
		},
		openCart() {
			this.isCartOpen = true;
		},
		closeCart() {
			this.isCartOpen = false;
		},
		toggleCart() {
			this.isCartOpen = !this.isCartOpen;
		},
		async removeFromBasket(lineNo) {
			const basket = this.$store.basket as Basket;
			const res = await BasketService.removeFromBasket({ body: { basketId: basket.id, lineNo: lineNo } });
			Alpine.store("basket", res.data);
			await getOrderTotal();
		},
		async subtractQuantity(item) {
			if (!isWebCustomer) {
				const basket = this.$store.basket as Basket;
				const totalQuantity = basket.items.reduce((acc, x) => (acc += parseInt(x.quantity.toString())), 0);
				if (totalQuantity > 10) {
					item.quantity = 10;
					return;
				}
			}
			item.quantity = Math.max(1, item.quantity - 1);
			await this.updateBasketItem(item);
		},
		async addQuantity(item) {
			item.quantity = Math.max(item.quantity, 1);
			if (!isWebCustomer) {
				const basket = this.$store.basket as Basket;
				const totalQuantity = basket.items.reduce((acc, x) => (acc += parseInt(x.quantity.toString())), 0);
				if (totalQuantity >= 10) {
					const totalQuantityWithoutItem = totalQuantity - item.quantity;
					item.quantity = 10 - totalQuantityWithoutItem;
					return;
				}
			}
			item.quantity += 1;
			await this.updateBasketItem(item);
		},
		async updateBasketItem(item) {
			item.quantity = Math.max(item.quantity, 1);
			if (!isWebCustomer) {
				const basket = this.$store.basket as Basket;
				const totalQuantity = basket.items.reduce((acc, x) => (acc += parseInt(x.quantity.toString())), 0);
				if (totalQuantity > 10) {
					const totalQuantityWithoutItem = basket.items
						.filter((x) => x != item)
						.reduce((acc, x) => (acc += parseInt(x.quantity.toString())), 0);
					item.quantity = 10 - totalQuantityWithoutItem;
					return;
				}
			}

			clearTimeout(timeout);
			timeout = setTimeout(async () => {
				const res = await BasketService.upsertBasketItem({
					body: {
						partNo: item.partNo,
						mainPartNo: item.mainPartNo,
						productType: item.productType,
						quantity: item.quantity,
						isClimateScreen: false,
					},
				});
				Alpine.store("basket", res.data);
				await getOrderTotal();
				await addToCartTracking(item.partNo, item.quantity);
			}, 500);
		},
		async addToBasket(partNo, mainPartNo, productType, quantity) {
			this.addToCartMaxQuantityWarning = false;
			if (!isWebCustomer) {
				const basket = this.$store.basket as Basket;
				const totalQuantity = basket.items.reduce((acc, x) => (acc += x.quantity), 0);
				if (totalQuantity >= 10) {
					this.addToCartMaxQuantityWarning = true;
					setTimeout(() => {
						this.addToCartMaxQuantityWarning = false;
					}, 5000);
					return;
				}
			}
			const res = await BasketService.upsertBasketItem({
				body: {
					partNo: partNo,
					mainPartNo: mainPartNo,
					productType: productType,
					quantity: quantity,
					isClimateScreen: false,
				},
			});

			(Alpine.store("basketNotification") as BasketNotificationStore).showNotification(productType);
			Alpine.store("basket", res.data);
			await getOrderTotal();
			await addToCartTracking(partNo, quantity);
		},
		async removeCutMeasure(item, cutMeasure) {
			if (item.cutMeasures.length === 1) {
				const basket = this.$store.basket as Basket;
				const res = await BasketService.removeFromBasket({
					body: {
						basketId: basket.id,
						lineNo: item.lineNo,
					},
				});
				Alpine.store("basket", res.data);
				await getOrderTotal();
			} else {
				const cutMeasures = item.cutMeasures.filter((x) => x != cutMeasure);
				const totalQuantity = cutMeasures.reduce((acc, x) => (acc += parseInt(x.noOfPieces)), 0);
				const res = await BasketService.upsertBasketItem({
					body: {
						partNo: item.partNo,
						mainPartNo: item.mainPartNo,
						productType: item.productType,
						quantity: totalQuantity,
						isClimateScreen: false,
						cutMeasure: JSON.stringify(cutMeasures),
					},
				});
				Alpine.store("basket", res.data);
				await getOrderTotal();
				await addToCartTracking(item.partNo, item.quantity);
			}
		},
		async updateCutMeasure(item) {
			item.cutMeasures.forEach((x) => {
				let noOfPieces = parseFloat(x.noOfPieces);
				noOfPieces = Math.max(noOfPieces, 1);
				noOfPieces = Math.round(noOfPieces);
				x.noOfPieces = noOfPieces.toString();

				let cutLength = parseFloat(x.cutLength);
				cutLength = Math.max(cutLength, 0.1);
				x.cutLength = cutLength.toString();
			});
			clearTimeout(timeout);
			timeout = setTimeout(async () => {
				let totalQuantity = item.cutMeasures.reduce((acc, x) => (acc += parseInt(x.noOfPieces)), 0);
				totalQuantity = Math.ceil(totalQuantity);
				const res = await BasketService.upsertBasketItem({
					body: {
						partNo: item.mainPartNo,
						mainPartNo: item.mainPartNo,
						productType: item.productType,
						quantity: totalQuantity,
						isClimateScreen: false,
						cutMeasure: JSON.stringify(item.cutMeasures),
					},
				});
				Alpine.store("basket", res.data);
				await getOrderTotal();
				await addToCartTracking(item.partNo, totalQuantity);
			}, 500);
		},
		async subtractCutMeasureQuantity(item: BasketItem, cutMeasure: CutMeasure) {
			cutMeasure.noOfPieces = Math.max(1, parseInt(cutMeasure.noOfPieces) - 1).toString();
			await this.updateCutMeasure(item);
		},
		async addCutMeasureQuantity(item: BasketItem, cutMeasure: CutMeasure) {
			cutMeasure.noOfPieces = (parseInt(cutMeasure.noOfPieces) + 1).toString();
			await this.updateCutMeasure(item);
		},
		isCutMeasureDisabled(item: BasketItem, index: number) {
			const oneMeters = item.cutMeasures.reduce((acc, x) => (acc += parseFloat(x.cutLength) >= 1 ? 1 : 0), 0);
			if (oneMeters > 1) {
				return false;
			}

			if (item.cutMeasures.length == 1) {
				return false;
			}

			const cutMeasure = item.cutMeasures[index];
			if (parseFloat(cutMeasure.cutLength) < 1) {
				return false;
			}

			return true;
		},
	};
};
